import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Package, 
  ArrowLeft, 
  Search, 
  ChevronRight 
} from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { searchCustomers, getCustomerLedger } from '../../services/dashboardApi';
import billService from '../../services/billService';

// Report view components
import CustomerReportView from '../parties/CustomerReportView';
import ItemReportView from '../items/ItemReportView';
import SalesReportView from '../bills/SalesReportView';
import PurchaseReportView from '../bills/PurchaseReportView';
import BillReportView from '../bills/BillReportView';
import CashbookReports from '../cashbook/CashbookReports';
import CashbookReportDay from '../cashbook/CashbookReportDay';

const reportMeta = {
  customer_ledger: {
    titleKey: LangKeys.customer_ledger_statement_report,
    descKey: LangKeys.customer_ledger_report_desc,
    icon: Users,
    category: 'ledger'
  },
  supplier_ledger: {
    titleKey: LangKeys.supplier_ledger_statement_report,
    descKey: LangKeys.supplier_ledger_report_desc,
    icon: Users,
    category: 'ledger'
  },
  cashbook_report: {
    titleKey: LangKeys.cashbook_report_title,
    descKey: LangKeys.cashbook_report_desc,
    icon: Calendar,
    category: 'cashbook'
  },
  sales_report: {
    titleKey: LangKeys.sales_report,
    descKey: LangKeys.sales_report_desc,
    icon: TrendingUp,
    category: 'billing',
    premium: true
  },
  purchase_report: {
    titleKey: LangKeys.purchase_report,
    descKey: LangKeys.purchase_report_desc,
    icon: TrendingDown,
    category: 'billing',
    premium: true
  },
  bill_report: {
    titleKey: LangKeys.bills_summary_report,
    descKey: LangKeys.bills_summary_report_desc,
    icon: BarChart3,
    category: 'billing',
    premium: true
  },
  item_report: {
    titleKey: LangKeys.item_stock_sales_report,
    descKey: LangKeys.item_stock_sales_report_desc,
    icon: Package,
    category: 'inventory',
    premium: true
  }
};

const ReportsHome = ({ setActiveTab }) => {
  const { t } = useLang();
  
  // Navigation & Report active states
  const [activeReport, setActiveReport] = useState(null); // customer_ledger, supplier_ledger, cashbook_report, sales_report, purchase_report, bill_report, item_report
  const [selectedParty, setSelectedParty] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedReportDate, setSelectedReportDate] = useState(null);

  // Loaded Data lists
  const [parties, setParties] = useState([]);
  const [bills, setBills] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Loading States
  const [loadingParties, setLoadingParties] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);

  // Premium lock check
  const isPremiumLocked = (reportKey) => {
    const premiumLevel = parseInt(localStorage.getItem("moxpal_premium_level") || "0");
    if (['sales_report', 'purchase_report', 'bill_report', 'item_report'].includes(reportKey)) {
      return premiumLevel < 2;
    }
    return false;
  };

  // Fetch lists
  useEffect(() => {
    if (activeReport === 'customer_ledger' || activeReport === 'supplier_ledger') {
      const fetchParties = async () => {
        setLoadingParties(true);
        try {
          const res = await searchCustomers();
          if (res?.success) {
            setParties(res.customers || []);
          }
        } catch (err) {
          console.error('Error fetching parties:', err);
        }
        setLoadingParties(false);
      };
      fetchParties();
    }
  }, [activeReport]);

  useEffect(() => {
    const fetchAllBills = async () => {
      setLoadingBills(true);
      try {
        const res = await billService.fetchBills();
        if (res) setBills(res);
      } catch (err) {
        console.error('Error fetching bills:', err);
      }
      setLoadingBills(false);
    };
    fetchAllBills();
  }, []);

  const handleReportClick = (reportKey) => {
    setActiveReport(reportKey);
    setSelectedParty(null);
    setSearchQuery('');
  };

  const handlePartySelect = async (party) => {
    setLoadingEntries(true);
    setSelectedParty(party);
    try {
      const res = await getCustomerLedger(party.id);
      if (res?.success) {
        setEntries(res.data?.entries || []);
      }
    } catch (err) {
      console.error('Error fetching party ledger:', err);
    }
    setLoadingEntries(false);
  };

  // Filter parties by search & type
  const filteredParties = parties.filter(p => {
    const isTypeMatch = activeReport === 'customer_ledger' 
      ? p.type?.toLowerCase() === 'customer'
      : p.type?.toLowerCase() === 'supplier';
    const isSearchMatch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      p.mobile?.includes(searchQuery);
    return isTypeMatch && isSearchMatch;
  });

  const reports = Object.entries(reportMeta).map(([key, meta]) => ({
    key,
    title: t(meta.titleKey),
    desc: t(meta.descKey),
    icon: meta.icon,
    category: meta.category,
    premium: meta.premium
  }));

  // Render direct Sub-Reports
  if (selectedReportDate) {
    return <CashbookReportDay date={selectedReportDate} onClose={() => setSelectedReportDate(null)} />;
  }

  if (activeReport === 'cashbook_report') {
    return <CashbookReports onClose={() => setActiveReport(null)} onDayClick={(date) => setSelectedReportDate(date)} />;
  }

  if (activeReport === 'item_report' && !isPremiumLocked('item_report')) {
    return <ItemReportView onBack={() => setActiveReport(null)} />;
  }

  if (activeReport === 'sales_report' && !isPremiumLocked('sales_report')) {
    return <SalesReportView bills={bills} onBack={() => setActiveReport(null)} />;
  }

  if (activeReport === 'purchase_report' && !isPremiumLocked('purchase_report')) {
    return <PurchaseReportView bills={bills} onBack={() => setActiveReport(null)} />;
  }

  if (activeReport === 'bill_report' && !isPremiumLocked('bill_report')) {
    return <BillReportView bills={bills} onBack={() => setActiveReport(null)} />;
  }

  // Selected Party ledger report rendering
  if (selectedParty && entries && !loadingEntries) {
    return (
      <CustomerReportView 
        party={selectedParty} 
        entries={entries} 
        onBack={() => {
          setSelectedParty(null);
          setEntries([]);
        }} 
      />
    );
  }

  // Premium lock overlay inside reports view
  if (activeReport && isPremiumLocked(activeReport)) {
    const activeMeta = reportMeta[activeReport];
    return (
      <div style={lockStyles.overlay}>
        <div style={lockStyles.card}>
          <div style={lockStyles.crownContainer}>
            <span style={lockStyles.crown}>👑</span>
          </div>
          <div style={lockStyles.lockBadge}>
            🔒 {t(LangKeys.premium_feature) || 'Premium Feature'}
          </div>
          <h3 style={lockStyles.title}>
            {activeMeta ? t(activeMeta.titleKey) : ''}
          </h3>
          <p style={lockStyles.desc}>
            {t(LangKeys.premium_lock_desc)}
          </p>
          <div style={lockStyles.benefitsList}>
            <div style={lockStyles.benefitRow}>🧾 {t(LangKeys.benefit_financial_reports)}</div>
            <div style={lockStyles.benefitRow}>📦 {t(LangKeys.benefit_manage_stocks)}</div>
            <div style={lockStyles.benefitRow}>💻 {t(LangKeys.benefit_desktop_access)}</div>
          </div>
          <div style={lockStyles.btnRow}>
            <button 
              style={lockStyles.upgradeBtn}
              onClick={() => {
                setActiveReport(null);
                if (typeof setActiveTab === 'function') {
                  setActiveTab('Premium Plans');
                }
              }}
            >
              {t(LangKeys.upgrade_to_premium)}
            </button>
            <button 
              style={lockStyles.backBtn}
              onClick={() => setActiveReport(null)}
            >
              {t(LangKeys.back_to_dashboard)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Select Party search list for customer/supplier reports
  if ((activeReport === 'customer_ledger' || activeReport === 'supplier_ledger') && !selectedParty) {
    return (
      <div style={styles.partySelectContainer}>
        <div style={styles.partySelectHeader}>
          <button style={styles.backBtn} onClick={() => setActiveReport(null)}>
            <ArrowLeft size={20} />
          </button>
          <h3 style={styles.partySelectTitle}>
            {activeReport === 'customer_ledger' ? t(LangKeys.select_customer) : t(LangKeys.select_supplier)}
          </h3>
        </div>

        <div style={styles.searchBarContainer}>
          <Search size={18} color="#94A3B8" />
          <input
            type="text"
            placeholder={t(LangKeys.search_placeholder)}
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div style={styles.partiesList}>
          {loadingParties ? (
            <div style={styles.center}>
              <div style={styles.spinner}></div>
            </div>
          ) : filteredParties.length === 0 ? (
            <div style={styles.noParties}>{t(LangKeys.no_parties_found)}</div>
          ) : (
            filteredParties.map(party => (
              <div
                key={party.id}
                style={styles.partyItem}
                onClick={() => handlePartySelect(party)}
              >
                <div style={styles.partyLeft}>
                  <div style={styles.avatar}>
                    {party.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.partyInfo}>
                    <span style={styles.partyName}>{party.name}</span>
                    <span style={styles.partyMobile}>{party.mobile}</span>
                  </div>
                </div>
                <div style={styles.partyRight}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: parseFloat(party.balance) >= 0 ? '#10B981' : '#EF4444'
                  }}>
                    ₹{Math.abs(parseFloat(party.balance)).toLocaleString()}
                  </span>
                  <ChevronRight size={18} color="#94A3B8" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Loading entries for customer ledger
  if (loadingEntries) {
    return (
      <div style={styles.center}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.spinner} style={{ margin: '0 auto 16px auto', width: '40px', height: '40px', border: '3px solid #E2E8F0', borderTop: '3px solid #2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <p style={{ color: '#64748B', fontWeight: '600' }}>{t(LangKeys.loading_report_data)}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>{t(LangKeys.business_reports) || 'Business Reports'}</h2>
        <p style={styles.subtitle}>{t(LangKeys.business_reports_desc)}</p>
      </div>

      {/* LEDGER & CASHBOOK SECTION */}
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>{t(LangKeys.ledger_cashbook_reports)}</h3>
      </div>
      <div style={styles.grid}>
        {reports.filter(r => r.category === 'ledger' || r.category === 'cashbook').map((r, i) => (
          <div key={i} style={styles.card}>
            <div style={styles.iconBox}>
              {React.createElement(r.icon, { size: 24, color: '#2563EB' })}
            </div>
            <h3 style={styles.cardTitle}>{r.title}</h3>
            <p style={styles.cardDesc}>{r.desc}</p>
            <button style={styles.viewBtn} onClick={() => handleReportClick(r.key)}>
              {t(LangKeys.view_report)}
            </button>
          </div>
        ))}
      </div>

      {/* BILLING & INVENTORY SECTION */}
      <div style={{ ...styles.sectionHeader, marginTop: '40px' }}>
        <h3 style={styles.sectionTitle}>{t(LangKeys.billing_inventory_reports)}</h3>
      </div>
      <div style={styles.grid}>
        {reports.filter(r => r.category === 'billing' || r.category === 'inventory').map((r, i) => {
          const isLocked = isPremiumLocked(r.key);
          return (
            <div key={i} style={styles.card}>
              <div style={styles.iconBox}>
                {React.createElement(r.icon, { size: 24, color: isLocked ? '#D97706' : '#2563EB' })}
                {isLocked && (
                  <span style={styles.lockIconBadge}>👑</span>
                )}
              </div>
              <h3 style={styles.cardTitle}>{r.title}</h3>
              <p style={styles.cardDesc}>{r.desc}</p>
              <button 
                style={{
                  ...styles.viewBtn,
                  ...(isLocked ? styles.viewBtnLocked : {})
                }} 
                onClick={() => handleReportClick(r.key)}
              >
                {isLocked ? '🔒 ' + t(LangKeys.plan_premium_name) : t(LangKeys.view_report)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '24px', fontFamily: "'Outfit', sans-serif" },
  header: { marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '800', color: '#0F172A', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748B', marginTop: '4px' },
  sectionHeader: { marginBottom: '16px', borderLeft: '4px solid #2563EB', paddingLeft: '12px' },
  sectionTitle: { fontSize: '16px', fontWeight: '800', color: '#1E293B', margin: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' },
  card: { background: '#fff', padding: '24px', borderRadius: '20px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', position: 'relative' },
  iconBox: { width: '48px', height: '48px', background: '#EFF6FF', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', position: 'relative' },
  lockIconBadge: { position: 'absolute', top: '-6px', right: '-6px', fontSize: '12px' },
  cardTitle: { fontSize: '16px', fontWeight: '700', color: '#0F172A', margin: '0 0 8px 0' },
  cardDesc: { fontSize: '13px', color: '#64748B', lineHeight: '1.5', flex: 1, marginBottom: '20px' },
  viewBtn: { width: '100%', padding: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', fontWeight: '700', color: '#2563EB', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  viewBtnLocked: { color: '#D97706', background: '#FEF3C7', border: '1px solid #FDE68A' },
  
  // Party Selection View Styles
  partySelectContainer: { padding: '24px', background: '#fff', borderRadius: '24px', border: '1px solid #E2E8F0' },
  partySelectHeader: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  backBtn: { width: '40px', height: '40px', borderRadius: '12px', background: '#F1F5F9', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' },
  partySelectTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  searchBarContainer: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#F8FAFC', borderRadius: '14px', border: '1px solid #E2E8F0', marginBottom: '20px' },
  searchInput: { border: 'none', background: 'transparent', fontSize: '14px', outline: 'none', flex: 1, color: '#0F172A', width: '100%' },
  partiesList: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '500px', overflowY: 'auto' },
  partyItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', borderRadius: '14px', border: '1px solid #F1F5F9', cursor: 'pointer', transition: 'all 0.2s ease' },
  partyLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '700' },
  partyInfo: { display: 'flex', flexDirection: 'column' },
  partyName: { fontSize: '14px', fontWeight: '700', color: '#0F172A' },
  partyMobile: { fontSize: '12px', color: '#64748B' },
  partyRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  noParties: { padding: '40px', textAlign: 'center', color: '#64748B', fontWeight: '600' },
  
  spinner: { width: '30px', height: '30px', border: '3px solid #E2E8F0', borderTop: '3px solid #2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '100px 0', width: '100%' }
};

const lockStyles = {
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(15, 23, 42, 0.05)',
    padding: '40px 20px',
    borderRadius: '24px',
    width: '100%'
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
    padding: '40px',
    maxWidth: '520px',
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  crownContainer: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    boxShadow: '0 8px 20px rgba(245, 158, 11, 0.2)',
  },
  crown: {
    fontSize: '40px',
  },
  lockBadge: {
    backgroundColor: '#FEE2E2',
    color: '#EF4444',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 8px 0',
  },
  desc: {
    fontSize: '14px',
    color: '#64748B',
    lineHeight: '1.5',
    margin: '0 0 24px 0',
    fontWeight: '500',
  },
  benefitsList: {
    width: '100%',
    backgroundColor: '#F8FAFC',
    borderRadius: '16px',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '28px',
    border: '1px solid #E2E8F0',
  },
  benefitRow: {
    fontSize: '13px',
    color: '#334155',
    fontWeight: '600',
    textAlign: 'left',
  },
  btnRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  upgradeBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(217, 119, 6, 0.3)',
  },
  backBtn: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    color: '#64748B',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
};

export default ReportsHome;
